import { Component, OnInit } from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { finalize} from "rxjs/operators";

import { UserService  } from "src/services/user.service";
import { User } from "src/models/user.model";
import {AngularFirestore} from "@angular/fire/firestore";
import Swal from "sweetalert2";

import { AuthService } from "src/services/auth.service";
import { AngularFireAuth } from "@angular/fire/auth";

import { AccountService  } from "src/services/account.service";
import { PlanService  } from "src/services/plan.service";
import {Account, AccountUser, Plans, Device} from "src/models/account.model";
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import {AngularFireStorage} from "@angular/fire/storage";
import {Observable, combineLatest} from "rxjs";

@Component({
  selector: 'app-accounts-add',
  templateUrl: './accounts-add.component.html'
})
export class AccountsAddComponent implements OnInit {

  public userForm: FormGroup;
  dropdownUserSettings:IDropdownSettings;
  dropdownPlanSettings:IDropdownSettings;

  get color(): string {
    return this._color;
  }
  set color(color: string) {
    this._color = color !== "light" && color !== "dark" ? "light" : color;
  }
  private _color = "light";

  user: any = [];
  account: Account = <Account> {};
  userAuth: User;
  users: User[] = [];
  usersForDropdown: any[] = [];
  plans: Plans[] = [];
  plan: Plans = <Plans> {};

  selectedUser: any = [];
  selectedPlan: any = {};
  ownerId: string = "";

  // Device related properties
  devices: Device[] = [];
  deviceForm: FormGroup;
  isAddingDevice: boolean = false;

  // Image upload properties
  percentage: Observable<number>;
  snapshot: Observable<any>;
  downloadURL: Observable<string>;
  photoUrl: any;

  constructor(
    protected route : ActivatedRoute,
    private userService: UserService,
    public formBuilder: FormBuilder,
    public router: Router,
    public authService: AuthService,
    public afs: AngularFirestore,
    public afAuth: AngularFireAuth,
    private planService: PlanService,
    private accountService: AccountService,
    private storage: AngularFireStorage,
    ) {

    this.authService.user$.subscribe(user => this.userAuth = user);

    this.userForm = this.formBuilder.group({
      accountName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      address1: ['', Validators.required],
      city: ['', Validators.required],
      province: ['', Validators.required],
      phoneNumber: ['', Validators.required],
      accountUsers: [''],
      plans: [''],
      status: [false],
      photoUrl: [''],
      tin: [''],
      taxType: ['NON-TAX']
    });

    this.deviceForm = this.formBuilder.group({
      device_name: ['', Validators.required],
      device_model: ['', Validators.required],
      device_serial_number: ['', Validators.required]
    });

    // Get users and plans
    combineLatest([
      this.userService.getUsers(),
      this.planService.getPlans(),
      this.accountService.getAccounts()
    ]).subscribe(([users, plans, allAccounts]) => {
      this.users = users;
      this.plans = plans;
      this.processUsersForDropdown(users, allAccounts);
    });
  }

  ngOnInit(): void {

    this.selectedUser = [];
    this.devices = [];

    this.dropdownUserSettings = {
      singleSelection: false,
      idField: 'id',
      textField: 'fullName',
      selectAllText: 'Select All',
      unSelectAllText: 'UnSelect All',
      enableCheckAll: false,
      allowSearchFilter: true,
      searchPlaceholderText: "search user",
      noDataAvailablePlaceholderText: "user not found",
      disabledField: 'isDisabled'
    };

    this.dropdownPlanSettings = {
      singleSelection: true,
      idField: 'id',
      textField: 'name'
    };

  }

  // Process users to mark those already assigned to other accounts
  processUsersForDropdown(users: User[], allAccounts: Account[]) {
    this.usersForDropdown = users.map(user => {
      // Check if user is assigned to any account
      const isAssignedToAccount = allAccounts.some(acc => {
        if (!acc.accountUsers) return false;
        return Array.isArray(acc.accountUsers) && acc.accountUsers.some(accUser => accUser.id === user.uid);
      });

      // Add property to show user's assignment status
      const userForDropdown = {
        ...user,
        id: user.uid, // Use uid as id for consistency
        isDisabled: isAssignedToAccount,
        fullName: user.fullName + (isAssignedToAccount ? ' (assigned elsewhere)' : '')
      };
      
      return userForDropdown;
    });
  }

  onSelectedPlan(item: any) {
    let elementPlan = this.plans.map((obj) => {return obj.id; }).indexOf(item.id);
    this.plan = this.plans[elementPlan];
  }

  onSelectedUsers(item: any) {
    // No logging needed
  }

  setOwnership(id){
    if(id){
      this.ownerId = id;
      this.selectedUser.findIndex((obj: any, i) => {
        this.selectedUser[i].isOwner = obj.id === id;
      });
    }
  }

  // Device management methods
  toggleAddDevice() {
    this.isAddingDevice = !this.isAddingDevice;
    if (!this.isAddingDevice) {
      this.deviceForm.reset();
    }
  }

  addDevice() {
    if (this.deviceForm.valid) {
      const deviceData = this.deviceForm.value;
      const newDevice: Device = {
        id: this.generateRandomString(8),
        device_name: deviceData.device_name,
        device_model: deviceData.device_model,
        device_serial_number: deviceData.device_serial_number,
        date_registered: new Date()
      };
      
      this.devices.push(newDevice);
      this.deviceForm.reset();
      this.isAddingDevice = false;
    }
  }

  removeDevice(index: number) {
    this.devices.splice(index, 1);
  }

  uploadImage($event: Event) {
    if(this.authService.canDelete(this.userAuth)){
      let currentDate = Date.now();
      const element = $event.target as HTMLInputElement;
      const file: File = (element.files as FileList)[0];
      const filePath = `company_logo/${currentDate}`;
      const fileRef = this.storage.ref(filePath);
      const task = this.storage.upload(`company_logo/${currentDate}`, file);

      task.snapshotChanges()
        .pipe(
          finalize(() => {
            this.downloadURL = fileRef.getDownloadURL();
            this.downloadURL.subscribe(url => {
              if (url) {
                this.account.photoUrl = url;
              }
            });
          })
        )
        .subscribe(url => {
          if (url) {
            // Image upload progress
          }
        });
    }else{
      alert("You have no right to upload image. Please contact your administrator. Thank you!");
    }
  }

  get getControl(){
    return this.userForm.controls;
  }

  get getDeviceControl(){
    return this.deviceForm.controls;
  }

  async save(){

    if(this.userForm.valid){
      const sUsers: any = [];

      let account: Account =  {
        account_key: this.generateRandomString(11),
        account_name: this.userForm.get('accountName').value,
        owner_id: this.ownerId,
        email_address: this.userForm.get('email').value,
        phone_number: this.userForm.get('phoneNumber').value,
        address: {
          street_house_building_number: this.userForm.get('address1').value,
          city: this.userForm.get('city').value,
          province: this.userForm.get('province').value
        },
        status: this.userForm.get('status').value,
        photoUrl: this.account.photoUrl || null,
        plans: this.plan,
        accountUsers: sUsers,
        devices: this.devices,
        tin: this.userForm.get('tin').value,
        taxType: this.userForm.get('taxType').value,
        dateCreated: new Date(),
        settings: {zones: []}
      }

      let isExist = this.selectedUser.findIndex((obj) => { return obj.id === account.owner_id; }) != -1;
      if(!isExist){
        await Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Please assign ownership.'
        })
        return;
      }

      for ( let i = 0; i < this.selectedUser.length; i++ ){

        if(this.selectedUser.length > 1){
          this.selectedUser[i].isOwner = account.owner_id === this.selectedUser[i].id;
        }else{
          account.owner_id = this.selectedUser[i].id;
          this.selectedUser[i].isOwner = true;
        }

        let accountUser: AccountUser = {
          account_key: account.account_key,
          id: this.selectedUser[i].id,
          fullName: this.selectedUser[i].fullName,
          isOwner: this.selectedUser[i].isOwner
        }

        sUsers.push(accountUser);
        await this.accountService.addAccountUser(account.account_key, sUsers);
      }

      await Swal.fire({
        title: 'Creating account... please wait...',
        allowEscapeKey: false,
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: ()=>{

          Swal.showLoading()

          this.accountService.addAccount(account).then((res) =>{

            Swal.fire({
              title: 'Account successfully created.',
              allowEscapeKey: false,
              allowOutsideClick: false,
              showConfirmButton: false,
              timer: 3000,
              icon: 'success',
              didClose: ()=>{
                this.selectedUser = [];
                this.plans = [];
                this.devices = [];
                this.userForm.reset();
                this.deviceForm.reset();
                this.account = <Account>{};
                Swal.hideLoading();
              }
            });

          });

        }
      });

    }else{
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Please check all the required fields.'
      })
    }

  }

  generateRandomString(length: number) {
    let result = '';
    let characters = '0123456789';
    let charactersLength = characters.length;
    for ( let i = 0; i < length; i++ ) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

}
